import { Link, Outlet, useLocation } from '@tanstack/react-router';

import { useFeatureGate } from '~/hooks/useFeatureGate';
import { usePermission } from '~/hooks/usePermission';
import { useTenantAdmin } from '~/hooks/useTenantAdmin';
import { cn } from '~/utils/cn';

interface OrgNavItem {
  to: string;
  label: string;
  permission: string;
  /** FEATURE_GATES code; when set, the item only shows while its flag is on. */
  featureGate?: string;
}

const orgNavItems: OrgNavItem[] = [
  {
    to: '/subsidiary/roles',
    label: 'Role Management',
    permission: 'organization_roles',
  },
  {
    to: '/subsidiary/users',
    label: 'User Management',
    permission: 'organization_members',
  },
  {
    to: '/subsidiary/subsidiaries',
    label: 'Subsidiaries',
    permission: 'organization_subsidiaries',
  },
  {
    to: '/subsidiary/screen-access',
    label: 'Screen Access',
    permission: 'organization_screen_access',
  },
  {
    to: '/subsidiary/access-policy',
    label: 'Access Policy',
    permission: 'organization_abac_policies',
  },
  {
    to: '/subsidiary/access-control',
    label: 'Access Control',
    permission: 'organization_abac_users',
  },
  {
    to: '/subsidiary/sso',
    label: 'SSO',
    permission: 'organization_sso',
  },
  {
    to: '/subsidiary/products',
    label: 'Product Enrollment & Contracts',
    permission: 'organization_contracts',
  },
  {
    to: '/subsidiary/sync-schedule',
    label: 'Sync Schedule',
    permission: 'organization_sync_schedule',
  },
  {
    to: '/subsidiary/asset-access',
    label: 'Asset & CDN Access',
    permission: 'organization_asset_access',
  },
  // Shareable Google Ads connection links; hidden unless `ads-share-link` is on.
  {
    to: '/subsidiary/data-sharing',
    label: 'Data Sharing',
    permission: 'integrations_sharing',
    featureGate: 'integrations_sharing',
  },
];

/** Layout shell for the Subsidiary Settings section — renders the shared title, card nav menu, and inner page outlet. */
export function OrganizationLayout() {
  return (
    <div className="bg-gradient-page flex h-full flex-col gap-8 p-6">
      {/* Page header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-foreground text-2xl leading-tight font-semibold">
          Subsidiary Settings
        </h1>
        <p className="text-muted-foreground text-sm leading-5">
          Configure and manage your subsidiaries, customize settings, and maintain operational
          efficiency across your organization.
        </p>
      </div>

      {/* Two-column layout: card nav + content */}
      <div className="flex min-h-0 flex-1 gap-4">
        {/* Card nav menu */}
        <aside className="bg-card flex w-[28%] max-w-80 min-w-[200px] shrink-0 flex-col gap-1 self-start rounded-2xl p-4 shadow-sm">
          {orgNavItems.map((item) => (
            <OrgNavLink key={item.to} item={item} />
          ))}
        </aside>

        {/* Inner page content */}
        <div className="min-w-0 flex-1 overflow-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/** Single nav item — highlights active route with the brand accent, permission-gated. */
function OrgNavLink({ item }: { item: OrgNavItem }) {
  const hasPermission = usePermission(item.permission);
  const isTenantAdmin = useTenantAdmin();
  // Ungated items (no `featureGate`) resolve to isAllowed=true.
  const { isAllowed: isGateOpen } = useFeatureGate(item.featureGate ?? '');
  const location = useLocation();
  const isActive = location.pathname.startsWith(item.to);

  if (!hasPermission && !isTenantAdmin) return null;
  if (!isGateOpen) return null;

  return (
    <Link
      to={item.to}
      className={cn(
        'flex cursor-pointer items-center rounded-xl px-4 py-3 text-sm font-medium transition-colors',
        isActive
          ? 'bg-accent-main text-white'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {item.label}
    </Link>
  );
}
